/**
 * 后台门禁的判定逻辑（`/admin/**` 谁能进）。
 *
 * 为什么从 `router/index.ts` 里抽成单独一个模块：
 * `router/index.ts` 的模块顶层就 `createWebHistory()`，并且静态 import 了一批 `.vue`；
 * 而 vitest 跑在 node 环境、没有 vue 插件，import 它必然抛错。判定本身只依赖契约和
 * 认证状态快照，抽成这个**无副作用**的模块之后，测试才能用真实 vue-router +
 * `createMemoryHistory()` 把守卫装到真路由上，复现"整页深链冷启动"——
 * 那个竞态只有真导航跑得出来（守卫、`main.ts` 里不 await 的 `bootstrapAuth()`、
 * 认证状态机三者交错），纯函数测不出来。
 *
 * 依赖是**注入**的（而不是直接 import `auth-store`）：测试可以喂一个可控的认证快照与
 * 一个可控的"恢复完成"Promise，从而稳定地复现"恢复尚未完成"这一刻。
 */

import { isUserRole, type UserRole } from '@spirit/contracts';
import type { NavigationGuard, RouteMeta } from 'vue-router';

import type { AuthStatus } from '../lib/auth-store';

/** 后台页面的两种权限：管理员以上，或仅 ROOT。写成常量，避免同一个字面量数组在多条路由上各写一遍。 */
export const ADMIN_ROLES: readonly UserRole[] = ['admin', 'root'];
export const ROOT_ROLES: readonly UserRole[] = ['root'];

/** 守卫需要的最小认证快照：只声明它真正读的三个字段。 */
export interface GuardAuthSnapshot {
  status: AuthStatus;
  role: UserRole | null;
  hydrated: boolean;
}

export interface AdminGuardDeps {
  getAuthState: () => GuardAuthSnapshot;
  /** 等"恢复登录态"结束；见 `auth-store` 的 `whenAuthReady()`。 */
  whenAuthReady: () => Promise<void>;
}

/**
 * 读出一条路由声明的角色要求。
 *
 * 返回 `null` 表示"这是一条公开路由"（没声明 `meta.roles`）。
 * 声明了但格式不对（不是数组 / 混进了未知角色）时返回**空数组**而不是 `null`：
 * 把写坏的门禁当成公开路由，等于悄悄开了一个洞；当成"谁都进不去"，问题会立刻
 * 以 403 的形式暴露给改路由的人。
 */
export function requiredRolesOf(meta: RouteMeta): readonly UserRole[] | null {
  const raw = meta.roles;
  if (raw === undefined) return null;
  if (!Array.isArray(raw)) return [];
  const roles = raw.filter(isUserRole);
  return roles.length === raw.length ? roles : [];
}

/**
 * 造一个后台门禁。
 *
 * 三件必须记住的事：
 * 1. **必须 `await whenAuthReady()`，不能读 `hydrated` 标志**。冷启动打开
 *    `/admin/usage` 这样的深链时，`main.ts` 里那句 `void bootstrapAuth()` 还在飞，
 *    此刻状态是空的 `anonymous`；只读标志会立刻"判未登录"并把已登录的 ROOT 闪去登录页
 *    （task-8 的缺陷）。等待的是同一个单飞 Promise，所以并发导航只会打一次 `/auth/me`；
 *    恢复已结束时立即兑现，不会每次导航都重打一次注定失败或已经成功的请求。
 * 2. **回跳地址用 `fullPath`**，并且只在这一条分支上加 query：登录页要能把人送回
 *    他原本要去的后台页面，否则深链一进去就被丢到 /chat（登录页的 `redirect` 处理见 LoginView）。
 * 3. **角色不符去 `/admin/403`，不是 404**。404 页会说这个地址不存在，而地址明明存在、
 *    只是这个人没权限——把这两种情况混为一谈，用户会去反复核对 URL 而不是找管理员开权限。
 */
export function createAdminGuard(deps: AdminGuardDeps): NavigationGuard {
  return async (to) => {
    const required = requiredRolesOf(to.meta);
    if (required === null) {
      // 公开路由完全不受影响：这条早退保证现有页面一条行为都没变。
      return true;
    }

    await deps.whenAuthReady();

    const auth = deps.getAuthState();
    if (auth.status !== 'authenticated') {
      return { name: 'login', query: { redirect: to.fullPath } };
    }
    if (auth.role === null || !required.includes(auth.role)) {
      return { name: 'adminForbidden' };
    }
    // 前端只是"不让点进注定被服务端拒绝的页面"，服务端仍会独立判一次（@Roles 守卫）。
    return true;
  };
}
